import { Box, Theme, Typography, makeStyles } from '@material-ui/core';
import { WbTooltip } from '../WbTooltip';
import { CSSProperties, ReactNode } from 'react';
import { Helmet } from 'react-helmet';

type WbHeaderProps = {
  pageTitleOverride?: string;
  style?: CSSProperties;
  titleStyle?: CSSProperties;
  title: ReactNode;
  tooltip?: string;
  topbarActions?: ReactNode;
  children?: ReactNode;
  breadcrumbs?: ReactNode;
  subtitle?: string;
  headerStyle?: CSSProperties;
  leftTitleSlot?: ReactNode;
  titleContainerStyle?: CSSProperties;
  hidden?: boolean;
  /** Content to render in the start section after the title */
  startSlot?: ReactNode;
  /** Content to render in the center section (absolutely centered) */
  centerSlot?: ReactNode;
  /** Content to render in the end section (right) */
  endSlot?: ReactNode;
  variant?: 'default' | 'secondary';
  disableSpacer?: boolean;
};

type WbHeaderStyleProps = {
  variant: 'default' | 'secondary';
  sidebarPinned: boolean;
};

import { useContent } from '@mainstage/core-components';
import { configApiRef, useApi } from '@mainstage/core-plugin-api';
import clsx from 'clsx';
import { WbSupportButton } from '../WbSupportButton';
import { WittyAssistant } from '@agilelab/plugin-wb-witty-react';
import { useResponsiveMobile } from '../../hooks/useResponsiveMobile';
import { useEnabledFeatures } from '../../context';
import { useWbSidebarPinState, sidebarConfig } from '../WbSidebar';
import { SCROLLBAR_WIDTH } from '../../styles';

// height of header
export const WB_HEADER_HEIGHT = 56;
export const WB_HEADER_BREADCRUMBS_HEIGHT = 40;

const useStyles = makeStyles<Theme, WbHeaderStyleProps>(
  theme => ({
    header: {
      gridArea: 'pageHeader',
      width: '100%',
      position: 'relative',
    },
    topbarWrapper: ({ sidebarPinned }) => ({
      position: 'fixed',
      zIndex: 10,
      width: sidebarPinned
        ? `calc(100% - ${sidebarConfig.drawerWidthOpen}px)`
        : `calc(100% - ${sidebarConfig.drawerWidthClosed}px)`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      backgroundColor: theme.palette.background.default,
      borderBottom: `1px solid ${theme.palette.grey[200]}`,
      [theme.breakpoints.down(theme.breakpoints.values.sidebar_breakpoint)]: {
        position: 'relative',
        flexDirection: 'column',
        width: '100%',
        backgroundColor: 'transparent',
        borderBottom: 'none',
        paddingInlineEnd: 0,
      },
    }),
    breadcrumbsWrapper: {
      height: WB_HEADER_BREADCRUMBS_HEIGHT,
      [theme.breakpoints.down(theme.breakpoints.values.sidebar_breakpoint)]: {
        width: '100%',
        backgroundColor: theme.palette.background.default,
        borderBottom: `1px solid ${theme.palette.grey[200]}`,
        display: 'flex',
        height: '32px',
        justifyContent: 'space-between',
      },
    },
    topbarActionsWrapper: {
      display: 'flex',
      gap: theme.spacing(1),
      [theme.breakpoints.down(theme.breakpoints.values.sidebar_breakpoint)]: {
        width: '100%',
        padding: theme.spacing(1),
      },
    },
    headerContent: ({ variant, sidebarPinned }) => {
      const variants = {
        default: {
          position: 'relative' as const,
          width: '100%',
        },
        secondary: {
          background: 'linear-gradient(to right, #FAFAFABB 90%, #FAFAFA)',
          backdropFilter: 'blur(20px)',
          borderBottom: `1px solid ${theme.palette.grey[200]}`,
          position: 'fixed' as const,
          top: 0,
          right: SCROLLBAR_WIDTH,
          width: sidebarPinned
            ? `calc(100% - ${sidebarConfig.drawerWidthOpen}px - ${SCROLLBAR_WIDTH}px)`
            : `calc(100% - ${sidebarConfig.drawerWidthClosed}px - ${SCROLLBAR_WIDTH}px)`,
        },
      };

      return {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        padding: theme.spacing(1.5),
        alignItems: 'center',
        height: WB_HEADER_HEIGHT,
        zIndex: 9,
        ...variants[variant],
        [theme.breakpoints.down(theme.breakpoints.values.sidebar_breakpoint)]: {
          position: 'relative',
          width: '100%',
          paddingInline: theme.spacing(1),
          paddingBlock: theme.spacing(1),
          height: 'auto',
          display: 'flex',
          flexWrap: 'wrap',
          marginBottom: theme.spacing(1),
          right: 0,
        },
      };
    },
    headerContentWithBreadcrumbs: {
      marginTop: WB_HEADER_BREADCRUMBS_HEIGHT,
      [theme.breakpoints.down(theme.breakpoints.values.sidebar_breakpoint)]: {
        marginTop: '0px',
      },
    },
    headerTitleContainer: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      minWidth: 0,
    },
    centerSlot: {
      position: 'absolute',
      left: '50%',
      transform: 'translateX(-50%)',
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      pointerEvents: 'none',
      '& > *': {
        pointerEvents: 'auto',
      },
      [theme.breakpoints.down(theme.breakpoints.values.sidebar_breakpoint)]: {
        position: 'relative',
        order: 3,
        width: '100%',
      },
    },
    endSlot: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      marginLeft: 'auto',
    },
    supportsButtonsWrapper: {
      display: 'flex',
      height: WB_HEADER_BREADCRUMBS_HEIGHT,
    },
    title: {
      wordBreak: 'break-word',
      marginBottom: 0,
    },
  }),
  { name: 'WbHeader' },
);

type HeaderStyles = ReturnType<typeof useStyles>;

type TitleFragmentProps = {
  classes: HeaderStyles;
  pageTitle: string | ReactNode;
  style?: WbHeaderProps['titleStyle'];
  tooltip?: WbHeaderProps['tooltip'];
};

const TitleFragment = ({
  pageTitle,
  classes,
  tooltip,
  style,
}: TitleFragmentProps) => {
  const { contentRef } = useContent();

  const FinalTitle = (
    <Typography
      ref={contentRef}
      tabIndex={-1}
      className={classes.title}
      variant="h4"
      style={style}
    >
      {pageTitle}
    </Typography>
  );

  if (!tooltip) {
    return FinalTitle;
  }

  return (
    <WbTooltip title={tooltip} placement="top-start">
      {FinalTitle}
    </WbTooltip>
  );
};

/**
 * Witboost main header
 *
 * @public
 *
 */
export const WbHeader = (props: WbHeaderProps) => {
  const {
    children,
    pageTitleOverride,
    style,
    title,
    tooltip,
    breadcrumbs,
    titleStyle,
    headerStyle,
    titleContainerStyle,
    hidden,
    topbarActions,
    leftTitleSlot,
    startSlot,
    centerSlot,
    endSlot,
    variant = 'default',
    disableSpacer = false,
  } = props;

  const { isPinned } = useWbSidebarPinState();

  const classes = useStyles({ variant, sidebarPinned: isPinned });

  const configApi = useApi(configApiRef);
  const appTitle = configApi.getOptionalString('app.title') || 'Witboost';
  const documentTitle = pageTitleOverride || title;
  const pageTitle = title || pageTitleOverride;
  const titleTemplate = `${documentTitle} | %s | ${appTitle}`;
  const defaultTitle = `${documentTitle} | ${appTitle}`;

  const isMobile = useResponsiveMobile();
  const { data: features } = useEnabledFeatures();
  const helmet = (
    <Helmet titleTemplate={titleTemplate} defaultTitle={defaultTitle} />
  );
  if (hidden) {
    return helmet;
  }

  const showPlatformButtons = !isMobile;

  return (
    <>
      {helmet}
      <header style={style} className={classes.header}>
        <div className={classes.topbarWrapper}>
          {breadcrumbs && (
            <div className={classes.breadcrumbsWrapper}>{breadcrumbs}</div>
          )}
          {(topbarActions || showPlatformButtons) && (
            <div className={classes.topbarActionsWrapper}>
              {topbarActions}
              {showPlatformButtons && (
                <div className={classes.supportsButtonsWrapper}>
                  <WbSupportButton />
                  {features?.wittyAssistantEnabled && <WittyAssistant />}
                </div>
              )}
            </div>
          )}
        </div>
        {!isMobile && variant === 'secondary' && !disableSpacer && (
          <div style={{ height: breadcrumbs ? 96 : WB_HEADER_HEIGHT }} />
        )}
        <Box
          className={clsx(
            classes.headerContent,
            breadcrumbs && classes.headerContentWithBreadcrumbs,
          )}
          style={headerStyle}
        >
          <Box
            className={classes.headerTitleContainer}
            style={titleContainerStyle}
          >
            {leftTitleSlot && leftTitleSlot}
            <TitleFragment
              style={titleStyle}
              classes={classes}
              pageTitle={pageTitle}
              tooltip={tooltip}
            />
            {startSlot}
          </Box>
          {centerSlot && <Box className={classes.centerSlot}>{centerSlot}</Box>}
          {(endSlot || children) && (
            <Box className={classes.endSlot}>
              {endSlot}
              {children}
            </Box>
          )}
        </Box>
      </header>
    </>
  );
};
